/**
 * Live Mobile Search Grid Simulator
 * ---------------------------------
 * Builds a realistic Amazon/Etsy/Shopify-style 2-column mobile search feed in
 * which the seller's (downsampled, 150x150) product thumbnail is rendered next
 * to sample competitor cards - so it is immediately obvious whether the listing
 * looks tiny, washed out or off-brand in a real search result.
 */

export interface CompetitorCard {
  title: string;
  price: string;
  rating: number;
  reviews: string;
  badge?: string;
  shipping: string;
  /** Two-stop CSS gradient used as the "product photo" */
  from: string;
  to: string;
  /** Glyph drawn inside the fake product photo */
  glyph: string;
}

/** Six deterministic competitors - the user's card is inserted at `simSlot`. */
export const COMPETITORS: CompetitorCard[] = [
  {
    title: 'Handmade Ceramic Vase for Pampas Grass, Modern Farmhouse Decor',
    price: '$26.99',
    rating: 4.6,
    reviews: '1,284',
    badge: 'Best Seller',
    shipping: 'FREE delivery Thu, Oct 16',
    from: '#fde68a',
    to: '#f59e0b',
    glyph: '🏺'
  },
  {
    title: 'Ceramic Flower Vase Set of 3, Rustic Boho Centerpiece Table Decor',
    price: '$19.49',
    rating: 4.3,
    reviews: '456',
    shipping: 'FREE delivery Oct 17 - Oct 19',
    from: '#fbcfe8',
    to: '#db2777',
    glyph: '💐'
  },
  {
    title: 'Nordic Minimalist Bud Vase, Matte White Pottery (12")',
    price: '$34.00',
    rating: 4.8,
    reviews: '3,915',
    badge: 'Amazon',
    shipping: 'Prime: FREE delivery tomorrow',
    from: '#bfdbfe',
    to: '#2563eb',
    glyph: '🌱'
  },
  {
    title: 'Glass Bud Vase, Clear Acrylic Centerpiece for Wedding Tables',
    price: '$12.99',
    rating: 4.1,
    reviews: '872',
    badge: 'Sponsored',
    shipping: 'FREE delivery Fri, Oct 17',
    from: '#a5f3fc',
    to: '#0891b2',
    glyph: '🫧'
  },
  {
    title: 'Rustic Terracotta Planter with Drainage, Indoor Pot 6 inch',
    price: '$22.95',
    rating: 4.5,
    reviews: '2,043',
    shipping: 'FREE delivery Oct 18 - Oct 20',
    from: '#fecaca',
    to: '#dc2626',
    glyph: '🪴'
  }
];

export function starsMarkup(rating: number): string {
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
  const stars = '★★★★★';
  return `<span class="star-row" aria-label="${rating} out of 5 stars">
      <span class="stars-base">${stars}</span>
      <span class="stars-fill" style="width:${pct}%">${stars}</span>
    </span>`;
}

interface BaseCardData {
  title: string;
  price: string;
  rating: number;
  reviews: string;
  badge?: string;
  shipping: string;
}

function cardShell(data: BaseCardData, mediaHtml: string, isUser: boolean): string {
  const badge =
    data.badge === 'Sponsored'
      ? `<span class="sim-chip rounded px-1.5 py-0.5 text-[10px] font-medium sim-muted">Sponsored</span>`
      : data.badge === 'Best Seller'
        ? `<span class="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">Best Seller</span>`
        : data.badge === 'Amazon'
          ? `<span class="rounded bg-sky-600 px-1.5 py-0.5 text-[10px] font-bold text-white">Prime</span>`
          : '';

  return `<article class="sim-card relative flex flex-col overflow-hidden rounded-lg border ${
    isUser ? 'ring-2 ring-indigo-500' : ''
  }">
      ${isUser ? '<span class="absolute left-1.5 top-1.5 z-10 rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold text-white shadow">Your photo</span>' : ''}
      ${mediaHtml}
      <div class="flex flex-1 flex-col gap-1 p-2">
        <p class="clamp-2 text-[12px] leading-snug" style="color: var(--sim-text)">${escapeHtml(data.title)}</p>
        <div class="flex items-center gap-1">
          ${starsMarkup(data.rating)}
          <span class="text-[11px]" style="color: var(--sim-muted)">${data.reviews}</span>
        </div>
        <div class="flex items-center gap-1.5">
          <span class="text-[15px] font-bold" style="color: var(--sim-text)">${data.price}</span>
          ${badge}
        </div>
        <p class="text-[10px] leading-tight" style="color: var(--sim-muted)">${escapeHtml(data.shipping)}</p>
      </div>
    </article>`;
}

/** Static competitor card (fake product photo made of a gradient + glyph). */
export function competitorCardMarkup(card: CompetitorCard): string {
  const media = `<div class="relative aspect-square w-full" style="background: linear-gradient(135deg, ${card.from}, ${card.to})">
      <div class="absolute inset-0 grid place-items-center text-5xl drop-shadow-sm" aria-hidden="true">${card.glyph}</div>
    </div>`;
  return cardShell(card, media, false);
}

/** The seller's card: the exact 150x150 mobile thumbnail is drawn into `canvasId`. */
export function userCardMarkup(canvasId: string): string {
  const media = `<div class="relative aspect-square w-full bg-white">
      <canvas id="${canvasId}" width="150" height="150" class="h-full w-full" aria-label="Your product thumbnail"></canvas>
    </div>`;
  return cardShell(
    {
      title: 'Your listing — this is exactly how shoppers see it in search results',
      price: '$29.99',
      rating: 4.2,
      reviews: '87',
      shipping: 'FREE delivery Thu, Oct 16'
    },
    media,
    true
  );
}

export interface GridBuildOptions {
  simSlot: number;
  theme: 'light' | 'dark';
  hasActiveImage: boolean;
}

/** Builds the complete phone-frame simulator markup. */
export function buildSimulatorMarkup(options: GridBuildOptions): string {
  const cards: string[] = [];
  const slot = Math.max(0, Math.min(COMPETITORS.length, options.simSlot));

  COMPETITORS.forEach((card, index) => {
    if (index === slot && options.hasActiveImage) {
      cards.push(userCardMarkup('sim-user-thumb'));
    }
    cards.push(competitorCardMarkup(card));
  });
  if (slot >= COMPETITORS.length && options.hasActiveImage) {
    cards.push(userCardMarkup('sim-user-thumb'));
  }

  const positionOptions = [0, 1, 2, 3, 4, 5]
    .map(
      (i) =>
        `<option value="${i}" ${i === options.simSlot ? 'selected' : ''}>Position #${i + 1}</option>`
    )
    .join('');

  return `
  <div class="sim-surface mx-auto w-full max-w-[400px] rounded-[2rem] border p-2 phone-frame" data-theme="${options.theme}" data-sim-root>
    <div class="flex items-center justify-between px-4 pb-1 pt-2 text-[11px] font-semibold" style="color: var(--sim-text)">
      <span>9:41</span>
      <span class="flex items-center gap-1"><span aria-hidden="true">▮▮▮</span><span aria-hidden="true">Wi-Fi</span><span aria-hidden="true">87%</span></span>
    </div>

    <div class="flex items-center gap-2 px-3 py-2">
      <div class="sim-search flex flex-1 items-center gap-2 rounded-full px-3 py-2 text-[13px]" style="color: var(--sim-muted)">
        <span aria-hidden="true">🔍</span>
        <span>ceramic vase</span>
      </div>
      <span class="text-lg" style="color: var(--sim-text)" aria-hidden="true">🛒</span>
    </div>

    <div class="flex items-center justify-between px-3 pb-2 text-[11px]" style="color: var(--sim-muted)">
      <span>1-6 of over 4,000 results</span>
      <span class="sim-chip rounded-full px-2 py-0.5">Prime ✓</span>
    </div>

    <div class="grid grid-cols-2 gap-2 px-2 pb-3">
      ${cards.join('\n')}
    </div>

    <div class="sim-card flex items-center justify-around rounded-t-2xl border-t px-2 py-2 text-[10px]" style="color: var(--sim-muted)">
      <span class="flex flex-col items-center"><span class="text-base">🏠</span>Home</span>
      <span class="flex flex-col items-center"><span class="text-base">🔍</span>Search</span>
      <span class="flex flex-col items-center"><span class="text-base">🛒</span>Cart</span>
      <span class="flex flex-col items-center"><span class="text-base">👤</span>You</span>
    </div>
  </div>

  <div class="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs">
    <label class="sr-only" for="sim-position">Your listing position</label>
    <select id="sim-position" class="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
      ${positionOptions}
    </select>
    <button type="button" data-action="toggle-sim-theme" class="rounded-md border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
      ${options.theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
    </button>
  </div>`;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
